/**
 * Platform Admin API client — /platform/* endpoints.
 *
 * Only callable by users with an active platform_admins row (platform_role
 * claim on the session). Enforcement is server-side; the frontend uses the
 * `platformRole` authStore flag only for navigation + UI gating.
 */

import apiClient from '@/api/client';
import type { PlatformRole } from '@/stores/authStore';

// ────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────

export interface OverviewKpi {
  label: string;
  value: number;
  delta?: number | null;
  delta_label?: string | null;
}

export interface PlatformOverview {
  mrr_current: number;
  mrr_previous: number;
  net_new_mrr_current: number;
  active_subscriptions: number;
  trialing_subscriptions: number;
  past_due_subscriptions: number;
  trials_expiring_today: number;
  kpis: OverviewKpi[];
}

export interface MrrDailyPoint {
  date: string;
  mrr: number;
  net_new_mrr: number;
}

export interface SubscriptionEventRow {
  id: string;
  group_id: string;
  group_name: string | null;
  subscription_id: string;
  event_type: string;
  prior_plan_id: string | null;
  new_plan_id: string | null;
  prior_mrr: number | null;
  new_mrr: number | null;
  mrr_delta: number | null;
  currency: string;
  actor_type: string;
  reason_code: string | null;
  occurred_at: string;
}

export interface TenantListItem {
  group_id: string;
  group_name: string;
  group_email: string | null;
  country_code: string | null;
  created_at: string;
  subscription_id: string | null;
  status: string | null;
  plan_slug: string | null;
  plan_name: string | null;
  mrr: number;
  currency: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  failed_payment_count: number;
}

export interface TenantListResponse {
  items: TenantListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface TenantDetail {
  group_id: string;
  group_name: string;
  group_email: string | null;
  created_at: string;
  subscription: {
    id: string;
    status: string;
    billing_interval: string;
    trial_ends_at: string | null;
    current_period_end: string | null;
    canceled_at: string | null;
    cancel_reason: string | null;
    failed_payment_count: number;
    last_payment_failure_at: string | null;
    ai_enabled: boolean;
    ai_disabled_reason: string | null;
    ai_disabled_at: string | null;
  } | null;
  plan: {
    id: string;
    name: string;
    slug: string;
    base_price_monthly: number;
    currency: string;
  } | null;
  companies: Array<{
    id: string;
    name: string;
    short_code: string | null;
    country_code: string | null;
    created_at: string;
  }>;
  invoices: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    period_start: string | null;
    period_end: string | null;
    created_at: string;
    pdf_url: string | null;
  }>;
  events: Array<{
    id: string;
    event_type: string;
    actor_type: string;
    reason_code: string | null;
    reason_note: string | null;
    prior_mrr: number | null;
    new_mrr: number | null;
    mrr_delta: number | null;
    currency: string;
    occurred_at: string;
  }>;
}

export interface TrialBoardItem {
  group_id: string;
  group_name: string;
  subscription_id: string;
  plan_slug: string;
  plan_name: string;
  trial_ends_at: string | null;
  days_remaining: number | null;
  employee_count: number;
  activated: boolean;
  created_at: string;
}

export interface DunningQueueItem {
  group_id: string;
  group_name: string;
  subscription_id: string;
  plan_name: string;
  status: string;
  failed_payment_count: number;
  last_payment_failure_at: string | null;
  mrr_at_risk: number;
  currency: string;
}

export interface PlatformAdminItem {
  user_id: string;
  email: string;
  full_name: string;
  role: PlatformRole;
  granted_by: string | null;
  granted_at: string;
  revoked_at: string | null;
  mfa_enabled: boolean;
}

// ── S7: invoices, gateway health, emails, notes, impersonation ──

export interface PlatformInvoiceRow {
  id: string;
  subscription_id: string;
  group_id: string;
  group_name: string;
  amount: number;
  currency: string;
  status: string;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  pdf_url: string | null;
  stripe_invoice_id: string | null;
}

export interface PlatformInvoicesResponse {
  items: PlatformInvoiceRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface GatewaySummary {
  gateway: string;
  calls_24h: number;
  success_24h: number;
  failure_24h: number;
  timeout_24h: number;
  p50_latency_ms: number | null;
  p95_latency_ms: number | null;
  success_rate: number;
}

export interface GatewayFailureRow {
  id: number;
  gateway: string;
  operation: string;
  outcome: string;
  error_code: string | null;
  error_message: string | null;
  latency_ms: number | null;
  occurred_at: string;
}

export interface GatewayHealthResponse {
  summaries: GatewaySummary[];
  recent_failures: GatewayFailureRow[];
}

export interface EmailSendRow {
  id: number;
  group_id: string | null;
  group_name: string | null;
  recipient_email: string;
  template_key: string;
  subject: string;
  status: string;
  send_provider: string;
  provider_message_id: string | null;
  error_message: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  sent_at: string;
}

export interface EmailSendLogResponse {
  items: EmailSendRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface TenantNoteRow {
  id: number;
  admin_email: string;
  admin_name: string;
  note: string;
  occurred_at: string;
}

export interface ImpersonationResult {
  session_id: string;
  target_user_id: string;
  target_company_id: string;
  target_group_id: string;
  target_group_name: string;
  target_user_email: string;
  started_at: string;
  expires_at: string;
  consent_mode: string;
  write_mode: boolean;
  elevated_at: string | null;
}

export interface ActiveImpersonation {
  session_id: string;
  admin_user_id: string;
  admin_email: string;
  admin_name: string;
  target_group_id: string;
  target_user_id: string;
  started_at: string;
  expires_at: string;
  consent_mode: string;
  reason: string;
  write_mode: boolean;
  elevated_at: string | null;
  is_admin_perspective: boolean;
}

export interface ElevateImpersonationResult {
  session_id: string;
  write_mode: boolean;
  elevated_at: string | null;
  elevation_reason: string | null;
  already_elevated: boolean;
}

// ── S8: audit, flags, analytics ──

export interface AuditRow {
  id: number;
  admin_user_id: string;
  admin_email: string;
  admin_name: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  reason: string;
  ip_address: string | null;
  occurred_at: string;
}

export interface AuditResponse {
  items: AuditRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  description: string;
  updated_at: string | null;
  updated_by: string | null;
}

export interface ChurnReasonRow {
  reason_code: string;
  count: number;
}

export interface ChurnHistogramBucket {
  label: string;
  count: number;
}

export interface ChurnAnalysisResponse {
  window_days: number;
  total_canceled: number;
  total_churn_mrr: number;
  by_reason: ChurnReasonRow[];
  time_to_churn_histogram: ChurnHistogramBucket[];
}

export interface CohortRetentionRow {
  cohort_month: string;
  size: number;
  retention: Array<number | null>;
}

export interface CohortRetentionResponse {
  months: number;
  grid: CohortRetentionRow[];
}

export interface PlanMixRow {
  plan_id: string;
  plan_name: string;
  plan_slug: string;
  base_price_monthly: number;
  currency: string;
  active_count: number;
  trialing_count: number;
  mrr_contribution: number;
}

export interface PlanMixResponse {
  plans: PlanMixRow[];
  total_mrr: number;
  total_active: number;
  total_trialing: number;
}

// ────────────────────────────────────────────────────────────────────
// Attribution — backend /admin/signup-events*
// ────────────────────────────────────────────────────────────────────

export interface SignupEventRow {
  id: string;
  group_id: string;
  user_id: string | null;
  subscription_id: string | null;
  plan_slug_requested: string | null;
  plan_slug_granted: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
  landing_path: string | null;
  country_code: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface MarketingLeadRow {
  id: string;
  email: string;
  source: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
  landing_path: string | null;
  country_code: string | null;
  created_at: string;
}

// ── SaaS books — which company holds AnooreHR's own subscription revenue ──

export interface SaasBooksCompanyOption {
  company_id: string;
  company_name: string;
  short_code: string | null;
  group_id: string;
  group_name: string;
  base_currency: string | null;
  country_code: string | null;
}

export interface SaasBooksStatus {
  configured: boolean;
  source: 'db' | 'env' | null;
  ready: boolean;
  platform_company_id: string | null;
  platform_company_name: string | null;
  bank_account_code: string;
  deferred_revenue_account_code: string;
  revenue_account_code: string;
  missing_account_codes: string[];
  base_currency: string | null;
}

export interface SaasBooksConfigUpdate {
  platform_company_id: string;
  bank_account_code: string;
  deferred_revenue_account_code: string;
  revenue_account_code: string;
}

// Platform SaaS billing gateway credentials (platform_owner)
export type GatewayKey = 'stripe' | 'paystack' | 'flutterwave';

export interface PlatformGatewayCredentials {
  gateway: GatewayKey;
  configured: boolean;
  source: 'db' | 'env' | 'none';
  test_mode: boolean | null;
  secret_key_masked: string | null;
  public_key: string | null;
  webhook_secret_masked: string | null;
  encryption_key_masked: string | null;
  requires_encryption_key: boolean;
}

export interface PlatformGatewayCredentialsUpdate {
  secret_key?: string | null;
  public_key?: string | null;
  webhook_secret?: string | null;
  encryption_key?: string | null;
}

// Platform AI keys (Anthropic, Voyage, …)
export type AIKeyName = 'anthropic' | 'voyage';

export interface PlatformAIKey {
  name: AIKeyName;
  configured: boolean;
  source: 'db' | 'env' | 'none';
  value_masked: string | null;
}

export interface AttributionBucket {
  key: string | null;
  count: number;
}

export interface AttributionSummary {
  total: number;
  by_utm_source: AttributionBucket[];
  by_plan_requested: AttributionBucket[];
  by_country: AttributionBucket[];
}

// ────────────────────────────────────────────────────────────────────
// API
// ────────────────────────────────────────────────────────────────────

export const platformApi = {
  // Overview
  getOverview: () => apiClient.get<PlatformOverview>('/platform/overview'),

  getMrrTrend: (days: number = 30) =>
    apiClient.get<MrrDailyPoint[]>('/platform/mrr/trend', { params: { days } }),

  getRecentEvents: (limit: number = 20) =>
    apiClient.get<SubscriptionEventRow[]>('/platform/events/recent', {
      params: { limit },
    }),

  // Tenants
  listTenants: (params?: {
    search?: string;
    status?: string;
    plan_slug?: string;
    country_code?: string;
    limit?: number;
    offset?: number;
  }) => apiClient.get<TenantListResponse>('/platform/tenants', { params }),

  getTenant: (groupId: string) =>
    apiClient.get<TenantDetail>(`/platform/tenants/${groupId}`),

  // Trials
  listTrials: () => apiClient.get<TrialBoardItem[]>('/platform/trials'),

  extendTrial: (subscriptionId: string, extra_days: number, reason: string) =>
    apiClient.post(`/platform/trials/${subscriptionId}/extend`, {
      extra_days,
      reason,
    }),

  // Dunning
  listDunning: () => apiClient.get<DunningQueueItem[]>('/platform/dunning'),

  markContacted: (subscriptionId: string, reason: string) =>
    apiClient.post(`/platform/dunning/${subscriptionId}/mark-contacted`, { reason }),

  // AI kill-switch (per-tenant emergency disable; platform_support+)
  setTenantAIEnabled: (
    groupId: string,
    enabled: boolean,
    reason: string,
  ) =>
    apiClient.post<{
      group_id: string;
      subscription_id: string;
      ai_enabled: boolean;
      ai_disabled_reason: string | null;
      ai_disabled_at: string | null;
    }>(`/platform/tenants/${groupId}/ai-enabled`, { enabled, reason }),

  // Admins
  listAdmins: () => apiClient.get<PlatformAdminItem[]>('/platform/admins'),

  grantAdmin: (user_id: string, role: PlatformRole, reason: string) =>
    apiClient.post<PlatformAdminItem>('/platform/admins/grant', {
      user_id,
      role,
      reason,
    }),

  revokeAdmin: (user_id: string, reason: string) =>
    apiClient.delete(`/platform/admins/${user_id}`, { params: { reason } }),

  // Invoices (cross-tenant)
  listInvoices: (params?: { status?: string; limit?: number; offset?: number }) =>
    apiClient.get<PlatformInvoicesResponse>('/platform/invoices', { params }),

  // Gateway health
  getGatewayHealth: () =>
    apiClient.get<GatewayHealthResponse>('/platform/gateway-health'),

  // Platform SaaS billing gateway credentials
  getGatewayCredentials: (gateway: GatewayKey) =>
    apiClient.get<PlatformGatewayCredentials>(
      `/platform/billing/gateways/${gateway}/credentials`,
    ),

  putGatewayCredentials: (gateway: GatewayKey, body: PlatformGatewayCredentialsUpdate) =>
    apiClient.put<PlatformGatewayCredentials>(
      `/platform/billing/gateways/${gateway}/credentials`,
      body,
    ),

  deleteGatewayCredentials: (gateway: GatewayKey) =>
    apiClient.delete<PlatformGatewayCredentials>(
      `/platform/billing/gateways/${gateway}/credentials`,
    ),

  // Platform AI keys (Anthropic, Voyage) — platform_owner only
  getAIKey: (name: AIKeyName) =>
    apiClient.get<PlatformAIKey>(`/platform/ai-keys/${name}`),

  putAIKey: (name: AIKeyName, value: string) =>
    apiClient.put<PlatformAIKey>(`/platform/ai-keys/${name}`, { value }),

  deleteAIKey: (name: AIKeyName) =>
    apiClient.delete<PlatformAIKey>(`/platform/ai-keys/${name}`),

  // Email send log
  listEmails: (params?: {
    template_key?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) => apiClient.get<EmailSendLogResponse>('/platform/emails', { params }),

  // Tenant internal notes
  listTenantNotes: (groupId: string) =>
    apiClient.get<TenantNoteRow[]>(`/platform/tenants/${groupId}/notes`),

  addTenantNote: (groupId: string, note: string) =>
    apiClient.post(`/platform/tenants/${groupId}/notes`, { note }),

  // Impersonation
  startImpersonation: (
    target_group_id: string,
    reason: string,
    consent_mode: 'tenant_notified' | 'breakglass' = 'tenant_notified',
    ticket_ref?: string,
    target_company_id?: string,
  ) =>
    apiClient.post<ImpersonationResult>('/platform/impersonate/start', {
      target_group_id,
      target_company_id,
      reason,
      consent_mode,
      ticket_ref,
    }),

  elevateImpersonation: (reason: string, ticket_ref?: string) =>
    apiClient.post<ElevateImpersonationResult>('/platform/impersonate/elevate', {
      reason,
      ticket_ref,
    }),

  endImpersonation: () => apiClient.post('/platform/impersonate/end'),

  // S8 — audit, flags, analytics
  getAudit: (params?: {
    admin_user_id?: string;
    action_prefix?: string;
    target_type?: string;
    limit?: number;
    offset?: number;
  }) => apiClient.get<AuditResponse>('/platform/audit', { params }),

  listFlags: () => apiClient.get<FeatureFlag[]>('/platform/flags'),

  upsertFlag: (key: string, enabled: boolean, reason: string, description?: string) =>
    apiClient.put<FeatureFlag>(`/platform/flags/${key}`, {
      enabled,
      reason,
      description,
    }),

  getChurn: (days: number = 90) =>
    apiClient.get<ChurnAnalysisResponse>('/platform/analytics/churn', { params: { days } }),

  getCohort: (months: number = 6) =>
    apiClient.get<CohortRetentionResponse>('/platform/analytics/cohort', { params: { months } }),

  getPlanMix: () => apiClient.get<PlanMixResponse>('/platform/analytics/plans'),

  // Marketing → signup attribution. Moved from /admin/* to /platform/analytics/*
  // in Sprint 1 (ADR-0001). Requires platform_readonly role or higher.
  listMarketingLeads: (params?: {
    limit?: number;
    offset?: number;
    source?: string;
    utm_source?: string;
    since?: string;
    until?: string;
  }) =>
    apiClient.get<MarketingLeadRow[]>('/platform/analytics/leads', { params }),

  listSignupEvents: (params?: {
    limit?: number;
    offset?: number;
    utm_source?: string;
    plan_slug?: string;
    country_code?: string;
    since?: string;
    until?: string;
  }) => apiClient.get<SignupEventRow[]>('/platform/analytics/signups', { params }),

  getSignupAttributionSummary: (params?: { since?: string; until?: string }) =>
    apiClient.get<AttributionSummary>('/platform/analytics/attribution', { params }),

  // SaaS books — platform_finance+
  getSaasBooks: () =>
    apiClient.get<SaasBooksStatus>('/platform/saas-books'),
  putSaasBooks: (body: SaasBooksConfigUpdate) =>
    apiClient.put<SaasBooksStatus>('/platform/saas-books', body),
  searchSaasBooksCompanies: (q: string, limit = 20) =>
    apiClient.get<SaasBooksCompanyOption[]>('/platform/saas-books/companies', {
      params: { q, limit },
    }),
};
